const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundMoney = (value) => Number(toNumber(value).toFixed(2));

const roundQuantity = (value) => Number(toNumber(value).toFixed(4));

const roundPercent = (value) => Math.round(toNumber(value));

const buildBasePrices = ({
  units,
  referenceSalePrice = 0,
  referencePriceTwo = 0,
  referencePriceThree = 0,
  purchasePrice = 0,
}) => {
  const normalizedReferenceSalePrice = roundMoney(referenceSalePrice);
  const normalizedReferencePriceTwo = roundMoney(referencePriceTwo);
  const normalizedReferencePriceThree = roundMoney(referencePriceThree);

  if (!Array.isArray(units) || units.length === 0) {
    const fallbackSalePrice =
      normalizedReferenceSalePrice > 0
        ? normalizedReferenceSalePrice
        : calculatePriceFromUtility(purchasePrice, 0);

    return {
      baseSalePrice: fallbackSalePrice,
      basePriceTwo:
        normalizedReferencePriceTwo > 0 ? normalizedReferencePriceTwo : 0,
      basePriceThree:
        normalizedReferencePriceThree > 0 ? normalizedReferencePriceThree : 0,
    };
  }

  const seedUnit =
    units.find((unit) => toNumber(unit.quantity) > 0) || units[0];
  const seedQuantity = Math.max(toNumber(seedUnit?.quantity), 1);
  const seedPrice = roundMoney(seedUnit?.price);
  const seedPriceTwo = roundMoney(seedUnit?.price_two);
  const seedPriceThree = roundMoney(seedUnit?.price_three);

  const baseSalePrice =
    normalizedReferenceSalePrice > 0
      ? normalizedReferenceSalePrice
      : seedPrice > 0
      ? roundMoney(seedPrice * seedQuantity)
      : calculatePriceFromUtility(purchasePrice, 0);

  return {
    baseSalePrice,
    basePriceTwo:
      normalizedReferencePriceTwo > 0
        ? normalizedReferencePriceTwo
        : seedPriceTwo > 0
        ? roundMoney(seedPriceTwo * seedQuantity)
        : 0,
    basePriceThree:
      normalizedReferencePriceThree > 0
        ? normalizedReferencePriceThree
        : seedPriceThree > 0
        ? roundMoney(seedPriceThree * seedQuantity)
        : 0,
  };
};

export const calculatePriceFromUtility = (purchasePrice, utility) => {
  const normalizedPurchasePrice = toNumber(purchasePrice);
  return roundMoney(
    normalizedPurchasePrice +
      normalizedPurchasePrice * (roundPercent(utility) / 100),
  );
};

export const calculateUtilityFromPrice = (purchasePrice, salePrice) => {
  const normalizedPurchasePrice = toNumber(purchasePrice);
  if (normalizedPurchasePrice <= 0) {
    return 0;
  }

  return roundPercent(
    ((toNumber(salePrice) - normalizedPurchasePrice) /
      normalizedPurchasePrice) *
      100,
  );
};

export const getReferenceUnit = (units) => {
  if (!Array.isArray(units) || units.length === 0) {
    return null;
  }

  return [...units].sort(
    (current, next) => toNumber(next.quantity) - toNumber(current.quantity),
  )[0];
};

export const isReferenceUnit = (unit, units) => {
  const reference = getReferenceUnit(units);
  if (!reference || !unit) {
    return false;
  }

  return reference.idUnitMeasure === unit.idUnitMeasure;
};

export const syncAutomaticUnitMeasures = (
  units,
  purchasePrice,
  options = {},
) => {
  if (!Array.isArray(units) || units.length === 0) {
    return [];
  }

  const normalizedUnits = units.map((unit) => ({
    ...unit,
    quantity: roundQuantity(unit.quantity),
    price: roundMoney(unit.price),
    price_two: roundMoney(unit.price_two),
    price_three: roundMoney(unit.price_three),
    utility: roundPercent(unit.utility),
    utility_price_two: roundPercent(unit.utility_price_two),
    utility_price_three: roundPercent(unit.utility_price_three),
  }));

  const { baseSalePrice, basePriceTwo, basePriceThree } = buildBasePrices({
    units: normalizedUnits,
    referenceSalePrice: options.referenceSalePrice,
    referencePriceTwo: options.referencePriceTwo,
    referencePriceThree: options.referencePriceThree,
    purchasePrice,
  });

  return normalizedUnits.map((unit) => {
    const quantity = toNumber(unit.quantity);
    if (quantity <= 0) {
      return unit;
    }

    const equivalentPurchasePrice = roundMoney(
      toNumber(purchasePrice) / quantity,
    );

    const automaticPrice = roundMoney(baseSalePrice / quantity);
    const automaticPriceTwo = roundMoney(basePriceTwo / quantity);
    const automaticPriceThree = roundMoney(basePriceThree / quantity);

    return {
      ...unit,
      quantity,
      price: automaticPrice,
      price_two: automaticPriceTwo,
      price_three: automaticPriceThree,
      utility: calculateUtilityFromPrice(
        equivalentPurchasePrice,
        automaticPrice,
      ),
      utility_price_two: calculateUtilityFromPrice(
        equivalentPurchasePrice,
        automaticPriceTwo,
      ),
      utility_price_three: calculateUtilityFromPrice(
        equivalentPurchasePrice,
        automaticPriceThree,
      ),
    };
  });
};

export const buildDraftUnitPreview = ({ draftUnit, units, purchasePrice }) => {
  const candidate = {
    ...draftUnit,
    quantity: roundQuantity(draftUnit.quantity),
    price: roundMoney(draftUnit.price),
    price_two: roundMoney(draftUnit.price_two),
    price_three: roundMoney(draftUnit.price_three),
    utility: roundPercent(draftUnit.utility),
    utility_price_two: roundPercent(draftUnit.utility_price_two),
    utility_price_three: roundPercent(draftUnit.utility_price_three),
  };

  return syncAutomaticUnitMeasures([...units, candidate], purchasePrice, {
    referenceSalePrice: draftUnit.referenceSalePrice,
    referencePriceTwo: draftUnit.referencePriceTwo,
    referencePriceThree: draftUnit.referencePriceThree,
  }).find((unit) => unit.idUnitMeasure === candidate.idUnitMeasure);
};
